import {
  PreBookingTransactionDataInput,
  useCreatePreBookingMutation,
  useCreatePreBookingTransactionMutation,
} from 'drycut-graphql';
import { Button } from 'ui-components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckoutButton } from './CheckoutButton';
import usePaymentMethod from './usePaymentCreation';

type Props = {
  data: PreBookingTransactionDataInput;
  amount: number;
} & React.ComponentProps<typeof Button>;

export function PreBookingCheckoutButton({ amount, data, ...props }: Props): JSX.Element {
  const navigate = useNavigate();

  const [createPayment, { loading: creatingPayment }] = useCreatePreBookingTransactionMutation();
  const [createPreBooking, { loading: creatingPreBooking }] = useCreatePreBookingMutation();

  const [transactionError, setTransactionError] = useState<string>();
  const [datatransId, setDatatransId] = useState<string>();

  const { paymentMethod, isPaymentSkipped } = usePaymentMethod({
    manual: data?.manual || false,
    amount,
  });

  const handleSubmit = async () => {
    const response = await createPayment({
      variables: { input: { amount, paymentMethod, data } },
    });

    const payload = response.data?.createPreBookingTransaction;

    if (payload?.errors && payload?.errors?.length > 0) {
      setTransactionError(payload.errors[0].message);
    } else {
      setTransactionError('');
      const transaction = payload?.paymentTransaction;

      // If it's a skip payment, we can create the booking right away and redirect the user to the
      // booking details when it's done. Otherwise the redirect will be a hard one from the
      // backend, after Datatrans has done its thing.
      if (payload && isPaymentSkipped && transaction?.id) {
        const preBooking = await createPreBooking({
          variables: { input: { transactionId: transaction?.id } },
        });

        const response = preBooking.data?.createPreBooking;
        const uid = response?.preBooking?.uid;
        const errors = response?.errors;

        if (errors && errors.length > 0) {
          setTransactionError(errors[0].message);
          return;
        }

        navigate(`/pre_booking/${uid}`);
      }

      if (transaction?.datatransId) {
        setDatatransId(transaction.datatransId);
      }
    }
  };

  const loading = creatingPayment || creatingPreBooking || !!datatransId;

  return (
    <CheckoutButton
      onClick={handleSubmit}
      onCancel={() => setDatatransId('')}
      loading={loading}
      datatransId={datatransId}
      transactionError={transactionError}
      onErrorClose={() => setTransactionError('')}
      {...props}
    />
  );
}
